import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Badge } from '@v2/design-system';
import api from '@v2/lib/api';
import { useI18n } from '@v2/providers/I18nProvider';

type DealGl = {
    gl_value: number;
    sale_cancelled: boolean;
    lines: Array<{ product_code?: string | null; product_name?: string | null; quantity: number; line_total: number }>;
    beneficiaries: Array<{
        id: number;
        name: string;
        avatar_url?: string | null;
        rate_pct: number;
        total: number;
        rows: Array<{ installment_label: string; base: number; amount: number; competence?: string | null; status: string }>;
    }>;
};

const brl = (v: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 2 }).format(v ?? 0);

function fmtMonth(ym?: string | null) {
    if (!ym) return '—';
    const [y, m] = ym.split('-').map(Number);
    return new Date(y, (m ?? 1) - 1, 1).toLocaleDateString('pt-BR', { month: 'short', year: '2-digit' });
}

/** Card separado do rateio dos vendedores: só aparece quando a venda tem produto GL. */
export default function GluteleaderDealCard({ dealId }: { dealId: number }) {
    const { t } = useI18n();
    const query = useQuery({
        queryKey: ['gluteleader', 'deal', dealId],
        queryFn: () => api.get(`/commissioning/gluteleader/deals/${dealId}`).then((r) => r.data.data as DealGl | null),
        retry: false,
    });

    const data = query.data;
    if (!data) return null;

    return (
        <section className="rounded-xl border border-fuchsia-200 bg-white p-4 shadow-sm dark:border-fuchsia-900/50 dark:bg-zinc-900">
            <div className="flex items-start justify-between gap-3">
                <div>
                    <p className="text-sm font-bold text-fuchsia-900 dark:text-fuchsia-200">{t('v2.gluteleader_wallet.deal_card_title')}</p>
                    <p className="mt-0.5 text-xs text-gray-500">{t('v2.gluteleader_wallet.deal_card_hint')}</p>
                </div>
                <div className="text-right">
                    <p className="text-[10px] font-semibold uppercase text-gray-500">{t('v2.gluteleader_wallet.gl_value')}</p>
                    <p className="text-sm font-black tabular-nums">{brl(data.gl_value)}</p>
                </div>
            </div>

            {data.sale_cancelled && (
                <Badge color="danger" variant="flat" rounded="pill" size="sm" className="mt-2">
                    {t('v2.gluteleader_wallet.sale_cancelled')}
                </Badge>
            )}

            <p className="mt-3 text-[10px] font-semibold uppercase tracking-wide text-gray-500">{t('v2.gluteleader_wallet.products')}</p>
            <ul className="mt-1 space-y-0.5 text-xs">
                {data.lines.map((line, idx) => (
                    <li key={`${line.product_code}-${idx}`} className="flex justify-between gap-2">
                        <span className="truncate">{line.quantity}× {line.product_name ?? line.product_code}</span>
                        <span className="shrink-0 tabular-nums">{brl(line.line_total)}</span>
                    </li>
                ))}
            </ul>

            <div className="mt-3 space-y-3">
                {data.beneficiaries.map((b) => (
                    <div key={b.id} className="rounded-lg bg-fuchsia-50/60 p-3 dark:bg-fuchsia-950/20">
                        <div className="flex items-center justify-between gap-2">
                            <Link to={`/commissioning/gluteleader/beneficiaries/${b.id}`} className="flex items-center gap-2 text-sm font-semibold hover:underline">
                                {b.avatar_url && <img src={b.avatar_url} alt="" className="size-6 rounded-full object-cover" />}
                                {b.name}
                                <span className="text-xs font-normal text-gray-500">{b.rate_pct}%</span>
                            </Link>
                            <span className="text-sm font-black tabular-nums text-fuchsia-800 dark:text-fuchsia-200">{brl(b.total)}</span>
                        </div>
                        <ul className="mt-2 space-y-0.5 text-[11px] text-gray-600 dark:text-zinc-300">
                            {b.rows.map((row, idx) => (
                                <li key={`${row.installment_label}-${idx}`} className="flex justify-between gap-2">
                                    <span>
                                        {row.installment_label} · {fmtMonth(row.competence)}
                                        <span className="ml-1 text-gray-400">{t(`v2.gluteleader_wallet.status_${row.status}`)}</span>
                                    </span>
                                    <span className="tabular-nums">{brl(row.amount)}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                ))}
            </div>
        </section>
    );
}
